import _ from 'lodash'

import { ApplicationContext } from '../applicationContext'
import { JSONSchemaResolver, RefResolver } from '../../../resolvers/json-schema-resolver'

const MAX_DEPTH = 8

interface EntitySpec {
  mixins?: string[]
}

/** Decides which property paths are emitted. Gates emission only — the walk still descends into filtered-out objects. */
export type PropertyPathFilter = (propertySchema: any, pathParts: string[]) => boolean

const INCLUDE_ALL: PropertyPathFilter = () => true

/**
 * Candidate munging target paths (`<alias>.<namespace>.<property>`) from a payload config's `entities`
 * dictionary, for Property Map target-path autocomplete. Cache-only and best-effort: uncached schemas
 * just yield fewer suggestions, and the field stays free-form.
 */
export function ingestionTargetPaths(
  this: ApplicationContext,
  entities: Record<string, EntitySpec> | null | undefined
): string[] {
  const store = (this as ApplicationContext)?.getStore()
  const plain = normalizeEntities(entities)
  if (!store || typeof store.getRecord !== 'function' || _.isEmpty(plain)) {
    return []
  }

  // Cache-only resolver: resolveReference reads from the store, never fetches remotely.
  const resolver = new JSONSchemaResolver({ getStore: () => store })

  const paths: string[] = []
  for (const alias of Object.keys(plain)) {
    const mixins = _.get(plain[alias], 'mixins', [])
    if (!Array.isArray(mixins)) {
      continue
    }
    for (const uri of mixins) {
      if (!isSchemaUri(uri)) {
        continue
      }
      const schema = store.getRecord(uri)
      if (!schema) {
        continue
      }
      paths.push(...schemaPropertyPaths(schema, INCLUDE_ALL, resolver).map((path) => `${alias}.${path}`))
    }
  }
  return _.uniq(paths).sort()
}

/** Metadata URIs of schemas currently in the client store, for entity-type/mixin autocomplete (free-form for anything missing). */
export function entityTypeUris(this: ApplicationContext): string[] {
  const store = (this as ApplicationContext)?.getStore()
  if (!store || typeof store.getSchemas !== 'function') {
    return []
  }
  return Object.keys(store.getSchemas() || {}).sort()
}

function normalizeEntities(entities: any): Record<string, EntitySpec> {
  return _.isPlainObject(entities) ? entities : {}
}

// Skip blank/half-typed mixin values while the config is being edited.
function isSchemaUri(uri: any): uri is string {
  return typeof uri === 'string' && uri.startsWith('/') && uri.endsWith('.json')
}

/*
 * Walk each namespace block of a materialized entity-type schema into `<namespace>.<property>` paths
 * (bare namespaces excluded). `filter` selects which properties are emitted; `resolver`, when given,
 * follows `$ref`s into nested definitions — synchronous, cache-backed, unresolvable refs skipped.
 */
export function schemaPropertyPaths(
  entitySchema: any,
  filter: PropertyPathFilter = INCLUDE_ALL,
  resolver?: RefResolver
): string[] {
  const properties = entitySchema?.get?.('properties') ?? entitySchema?.properties
  const out: string[] = []
  _.forEach(properties, (namespaceSchema: any, namespace: string) => {
    const namespaceProps = _.get(namespaceSchema, 'properties')
    if (namespaceProps) {
      out.push(...walkProperties(namespaceProps, [namespace], 1, filter, resolver, entitySchema, new Set()))
    }
  })
  return out
}

function walkProperties(
  properties: any,
  prefix: string[],
  depth: number,
  filter: PropertyPathFilter,
  resolver: RefResolver | undefined,
  context: any,
  seenRefs: Set<string>
): string[] {
  if (!properties || depth > MAX_DEPTH) {
    return []
  }
  const out: string[] = []
  _.forEach(properties, (propertySchema: any, key: string) => {
    if (!_.isObject(propertySchema)) {
      return
    }
    const prop = propertySchema as any
    const path = [...prefix, key]
    if (filter(propertySchema, path)) {
      out.push(path.join('.'))
    }
    // Inline object / array-of-objects.
    if (prop.properties) {
      out.push(...walkProperties(prop.properties, path, depth + 1, filter, resolver, context, seenRefs))
    }
    if (prop.items?.properties) {
      out.push(...walkProperties(prop.items.properties, path, depth + 1, filter, resolver, context, seenRefs))
    }
    // seenRefs breaks reference cycles; MAX_DEPTH bounds overall depth.
    const ref: string | undefined = prop.$ref ?? prop.items?.$ref
    if (ref && resolver && !seenRefs.has(ref)) {
      const resolved = resolveRefProperties(resolver, context, ref)
      if (resolved) {
        out.push(
          ...walkProperties(
            resolved.properties,
            path,
            depth + 1,
            filter,
            resolver,
            resolved.context,
            new Set([...seenRefs, ref])
          )
        )
      }
    }
  })
  return out
}

/* Resolve a `$ref` to the property block it introduces (cache-only, best-effort); undefined when unresolvable. */
function resolveRefProperties(
  resolver: RefResolver,
  context: any,
  ref: string
): { properties: any; context: any } | undefined {
  try {
    const { schema, context: nextContext } = resolver.resolveReference(context, ref)
    const properties =
      schema?.get?.('properties') ?? _.get(schema, 'properties') ?? _.get(schema, 'items.properties')
    if (properties) {
      return { properties, context: nextContext ?? context }
    }
  } catch {
    // Unresolvable ref — skip it.
  }
  return undefined
}
